"use client"

import { useState, useEffect } from "react"
import {
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaCar,
  FaInfoCircle,
  FaExclamationTriangle,
} from "react-icons/fa"
import Sidebar from "../SidebarInAdminPanel/Sidebar"
import { fetchAllRides } from "../../Controllers/rideController"

const RidePage = () => {
  const [rides, setRides] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedRide, setSelectedRide] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const openModal = (ride) => {
    setSelectedRide(ride)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setSelectedRide(null)
    setIsModalOpen(false)
  }

  useEffect(() => {
    const loadRides = async () => {
      try {
        const fetchedRides = await fetchAllRides()

        // Transform Firestore data to match your UI structure
        const transformedRides = fetchedRides.map((ride) => ({
          id: ride.id,
          driver: ride.riderEmail?.split("@")[0] || "Driver",
          commuteType: ride.transportType || "Local",
          from: ride.departureLocation?.formatted_address || "Unknown departure",
          to: ride.destinationLocation?.formatted_address || "Unknown destination",
          date: formatDate(ride.departureDate),
          time: formatTime(ride.departureTime),
          imageUrl: ride.profileImage || "/Images/default-profile.jpeg",
          createdAt: ride.createdAt?.toDate() || new Date(),
          specificInstructions: ride.specificAddressInstructions || "No specific instructions",
          specialInstructions: ride.specialInstructions || "No special instructions",
        }))

        setRides(transformedRides)
      } catch (err) {
        console.error("Error loading rides:", err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadRides()
  }, [])

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-GB") // DD/MM/YYYY format
  }

  const formatTime = (timeString) => {
    if (!timeString) return "N/A"
    const time = new Date(timeString)
    return time.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  if (loading) {
    return (
      <div className="flex flex-col md:flex-row min-h-screen bg-gradient-to-r from-[#0AD1C8] to-[#073235]">
        <Sidebar />
        <div className="flex-1 p-8">
          <h1 className="text-4xl font-bold mb-8 text-white">All Rides</h1>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-16 h-16 bg-gray-200 rounded-full mr-4"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                    </div>
                  </div>
                  <div className="h-20 bg-gray-200 rounded-xl mb-4"></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="h-16 bg-gray-200 rounded-xl"></div>
                    <div className="h-16 bg-gray-200 rounded-xl"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col md:flex-row min-h-screen bg-gradient-to-r from-[#0AD1C8] to-[#073235]">
        <Sidebar />
        <div className="flex-1 p-8">
          <h1 className="text-4xl font-bold mb-8 text-white">All Rides</h1>
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
            <strong>Error:</strong> {error}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gradient-to-r from-[#0AD1C8] to-[#073235]">
      <Sidebar />
      <div className="flex-1 p-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-white">All Rides</h1>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-md mb-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
              <div className="flex items-center">
                <div>
                  <p className="text-blue-600 font-semibold text-lg tracking-wide">Total Rides</p>
                  <p className="text-3xl font-bold text-blue-800">{rides.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
              <div className="flex items-center">
                <div>
                  <p className="text-green-600 font-semibold text-lg  tracking-wide">Active Routes</p>
                  <p className="text-3xl font-bold text-green-800">{rides.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
              <div className="flex items-center">
                <div>
                  <p className="text-purple-600 font-semibold text-lg tracking-wide">Active Drivers</p>
                  <p className="text-3xl font-bold text-purple-800">{new Set(rides.map((r) => r.driver)).size}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Rides Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {rides.length > 0 ? (
              rides.map((ride) => (
                <div
                  key={ride.id}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-gray-400 group hover:border-teal-500 hover:scale-[1.02] cursor-pointer"
                  onClick={() => openModal(ride)}
                >
                  {/* Header with Driver Info */}
                  <div className="bg-gradient-to-r from-teal-500 to-blue-500 p-6 text-white">
                    <div className="flex items-center">
                      <img
                        src={ride.imageUrl || "/placeholder.svg"}
                        alt={ride.driver}
                        className="w-16 h-16 object-cover rounded-full border-4 border-white/30 mr-4"
                        onError={(e) => {
                          e.target.src = "/Images/default-profile.jpeg"
                        }}
                      />
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-1">{ride.driver}</h3>
                        <div className="flex items-center">
                          <FaCar className="mr-2 text-sm" />
                          <span className="text-white/90 font-medium">{ride.commuteType}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {/* Route Section */}
                    <div className="bg-gray-50 rounded-xl p-4 mb-6">
                      <div className="space-y-4">
                        {/* From */}
                        <div className="flex items-start">
                          <div className="flex-shrink-0 w-4 h-4 bg-green-500 rounded-full mt-1 mr-3 ring-4 ring-green-100"></div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-green-600 uppercase tracking-wide font-semibold mb-1">From</p>
                            <p className="text-sm text-gray-800 font-medium leading-tight">{ride.from}</p>
                          </div>
                        </div>

                        {/* Connecting Line */}
                        <div className="ml-2 w-0.5 h-6 bg-gradient-to-b from-green-500 to-red-500"></div>

                        {/* To */}
                        <div className="flex items-start">
                          <FaMapMarkerAlt className="flex-shrink-0 text-red-500 mt-1 mr-3 text-sm" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-red-600 uppercase tracking-wide font-semibold mb-1">To</p>
                            <p className="text-sm text-gray-800 font-medium leading-tight">{ride.to}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Date and Time */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-2">
                          <FaCalendarAlt className="text-white text-sm" />
                        </div>
                        <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">Date</p>
                        <p className="text-lg font-bold text-blue-800">{ride.date}</p>
                      </div>

                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center">
                        <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-2">
                          <FaClock className="text-white text-sm" />
                        </div>
                        <p className="text-xs text-purple-600 font-semibold uppercase tracking-wide">Time</p>
                        <p className="text-lg font-bold text-purple-800">{ride.time}</p>
                      </div>
                    </div>

                    {/* Instructions Preview */}
                    {(ride.specificInstructions !== "No specific instructions" ||
                      ride.specialInstructions !== "No special instructions") && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-center">
                          <FaInfoCircle className="text-yellow-600 mr-2 text-sm" />
                          <span className="text-yellow-800 text-sm font-medium">Additional instructions available</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaCar className="text-gray-400 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No rides available</h3>
                <p className="text-gray-500">Check back later for new ride postings.</p>
              </div>
            )}
          </div>
        </div>

        {/* Ride Detail Modal */}
        {isModalOpen && selectedRide && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-teal-500 to-blue-500 p-8 text-white relative">
                <button
                  onClick={closeModal}
                  className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div className="flex items-center">
                  <img
                    src={selectedRide.imageUrl || "/placeholder.svg"}
                    alt={selectedRide.driver}
                    className="w-20 h-20 object-cover rounded-full border-4 border-white/30 mr-6"
                  />
                  <div>
                    <h2 className="text-3xl font-bold mb-2">{selectedRide.driver}</h2>
                    <div className="flex items-center text-white/90">
                      <FaCar className="mr-2" />
                      <span className="text-lg">{selectedRide.commuteType}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-8">
                {/* Route Details */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">Route Details</h3>
                  <div className="bg-gray-50 rounded-2xl p-8">
                    <div className="space-y-6">
                      {/* From Location */}
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full mt-1 mr-4 ring-4 ring-green-100 flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                        <div className="flex-1">
                          <p className="text-green-600 font-semibold text-sm uppercase tracking-wide mb-1">
                            Departure Location
                          </p>
                          <p className="text-xl font-semibold text-gray-800">{selectedRide.from}</p>
                        </div>
                      </div>

                      {/* Connecting Line */}
                      <div className="ml-3 w-0.5 h-12 bg-gradient-to-b from-green-500 to-red-500"></div>

                      {/* To Location */}
                      <div className="flex items-start">
                        <FaMapMarkerAlt className="flex-shrink-0 text-red-500 mt-2 mr-4 text-xl" />
                        <div className="flex-1">
                          <p className="text-red-600 font-semibold text-sm uppercase tracking-wide mb-1">
                            Destination Location
                          </p>
                          <p className="text-xl font-semibold text-gray-800">{selectedRide.to}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Schedule Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 text-center">
                    <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FaCalendarAlt className="text-white text-2xl" />
                    </div>
                    <p className="text-blue-600 font-semibold text-sm uppercase tracking-wide mb-2">Departure Date</p>
                    <p className="text-3xl font-bold text-blue-800">{selectedRide.date}</p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 text-center">
                    <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FaClock className="text-white text-2xl" />
                    </div>
                    <p className="text-purple-600 font-semibold text-sm uppercase tracking-wide mb-2">Departure Time</p>
                    <p className="text-3xl font-bold text-purple-800">{selectedRide.time}</p>
                  </div>
                </div>

                {/* Instructions */}
                <div className="space-y-4">
                 {/* Always show Address Instructions */}
<div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
  <div className="flex items-start">
    <FaInfoCircle className="text-blue-500 mt-1 mr-3 text-lg" />
    <div>
      <h4 className="text-blue-800 font-semibold mb-2">Address Instructions</h4>
      <p className="text-blue-700">{selectedRide.specificInstructions}</p>
    </div>
  </div>
</div>


               {/* Always show Special Instructions */}
<div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
  <div className="flex items-start">
    <FaExclamationTriangle className="text-yellow-500 mt-1 mr-3 text-lg" />
    <div>
      <h4 className="text-yellow-800 font-semibold mb-2">Special Instructions</h4>
      <p className="text-yellow-700">{selectedRide.specialInstructions}</p>
    </div>
  </div>
</div>
                </div>

              
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default RidePage

